<template>
  <div class="home">
    <div class="wrapper--home container">
      <div class="main-searchbar">
        <div class="main-searchbar--input">
          <h2 class="main-searchbar__title">
            {{ $t("searchPage.header") }}
          </h2>

          <form @submit.prevent="submitSearch" class="main-searchbar__form">
            <div
              class="main-searchbar__location-input searchbar__input-wrapper"
            >
              <LandingLocationSelector
                name="location"
                @selectLocation="selectLocation"
              />
            </div>

            <div class="main-searchbar__checkin-input searchbar__input-wrapper">
              <DatepickerFoldout @selectDates="selectDates" />
            </div>
            <div class="main-searchbar__meta-input searchbar__input-wrapper">
              <div class="columns is-mobile">
                <div class="column">
                  <div class="searchbar__icon">
                    <i
                      class="fas fa-person"
                      :title="$t('searchFilters.numberOfPersons')"
                      aria-hidden="true"
                    ></i>
                  </div>
                  <input
                    id="adults"
                    type="number"
                    class="searchbar__input"
                    min="1"
                    max="8"
                    :placeholder="$t('searchFilters.numberOfRooms')"
                    v-model="rooms"
                  />
                </div>
              </div>
            </div>
            <div class="main-searchbar__submit">
              <button
                type="submit"
                class="c-button c-button--primary"
                :disabled="location == ''"
              >
                {{ $t("search") }}
              </button>
              <!-- <router-link
                class="c-button c-button--secondary c-button--fullwidth is-block mt-3"
                :to="{
                  name: 'openRequest'
                }"
              >
                {{ $t("openRequest.differentDatesLink") }}
              </router-link> -->
            </div>
          </form>
        </div>
      </div>

      <div class="main-favorites">
        <FavoriteHotels></FavoriteHotels>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted } from "vue";
import introJs from "intro.js";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const EXPEDIA_PORTAL_UPDATE_COOKIE =
  "expediaPortalUpdate-2026-06-dontShowAgain=true";

const hasCookie = (cookieNameWithValue) =>
  document.cookie
    .split(";")
    .map((entry) => entry.trim())
    .includes(cookieNameWithValue);

const setSeenCookie = (cookieNameWithValue) => {
  document.cookie = `${cookieNameWithValue}; path=/; max-age=31536000; Secure; SameSite=Lax`;
};

const createIntroOptions = (steps) => ({
  nextLabel: t("next"),
  skipLabel: t("skip"),
  prevLabel: t("back"),
  doneLabel: t("done"),
  steps
});

const startDismissibleIntro = (cookieNameWithValue, steps) => {
  if (hasCookie(cookieNameWithValue)) {
    return;
  }

  const intro = introJs();
  const markSeen = () => setSeenCookie(cookieNameWithValue);

  intro.setOptions(createIntroOptions(steps));
  intro.oncomplete(markSeen);
  intro.onexit(markSeen);
  intro.onskip(markSeen);
  intro.start();
};

onMounted(() => {
  const intro = introJs();

  intro.setOptions({
    dontShowAgain: true,
    dontShowAgainLabel: t("dontShowAgain"),
    nextLabel: t("next"),
    skipLabel: t("skip"),
    prevLabel: t("back"),
    doneLabel: t("done"),
    steps: [
      {
        intro: t("walkthrough.welcome")
      },
      {
        element: document.querySelector(".main-searchbar"),
        intro: t("walkthrough.step1")
      },
      {
        element: document.querySelector(".main-searchbar__location-input"),
        intro: t("walkthrough.step1a")
      },
      {
        element: document.querySelector(".main-searchbar__checkin-input"),
        intro: t("walkthrough.step1b")
      },
      {
        element: document.querySelector(".main-searchbar__meta-input"),
        intro: t("walkthrough.step1c")
      },
      {
        element: document.querySelector(".main-searchbar__submit"),
        intro: t("walkthrough.step1d")
      },
      //   {
      //     element: document.querySelector(".navbar"),
      //     intro: t("walkthrough.step2")
      //   },
      {
        element: document.querySelector(".dropdown.is-right"),
        intro: t("walkthrough.step3")
      },
      {
        element: document.querySelector(".main-favorites"),
        intro: t("walkthrough.step3a")
      },
      {
        element: document.querySelector(".contact-container"),
        intro: t("walkthrough.step4")
      }
    ]
  });

  intro.onafterchange(() => {
    const checkbox = document.querySelector("#introjs-dontShowAgain");
    if (checkbox) {
      checkbox.parentElement.style.display = "none";
    }
  });

  intro.onexit(() => {
    document.cookie =
      "introjs-dontShowAgain=true; path=/; max-age=31536000; Secure; SameSite=Lax";
  });

  intro.start();

  startDismissibleIntro(EXPEDIA_PORTAL_UPDATE_COOKIE, [
    {
      intro: `<b>${t("expediaPortalUpdatePopup.title")}</b><br><br>${t("expediaPortalUpdatePopup.paragraph1")}<br><br>${t("expediaPortalUpdatePopup.paragraph2")}<br><br>${t("expediaPortalUpdatePopup.paragraph3")}`
    }
  ]);
});

onUnmounted(() => {
  introJs().exit(true);
});
</script>

<script>
import LandingLocationSelector from "./components/landingpage/LandingLocationSelector.vue";
import DatepickerFoldout from "./components/landingpage/DatepickerFoldout.vue";
import FavoriteHotels from "./components/landingpage/FavoriteHotels.vue";
import GoogleLocationSearch from "./components/shared/GoogleLocationSearch";

export default {
  name: "Home",
  components: {
    // eslint-disable-next-line vue/no-unused-components
    GoogleLocationSearch,
    FavoriteHotels,
    LandingLocationSelector,
    DatepickerFoldout
  },
  data() {
    return {
      location: "",
      adults: 1,
      rooms: 1,
      selectedLocationObj: {},
      dates: "",
      hideNonLocation: false
    };
  },
  methods: {
    selectLocation(location) {
      this.selectedLocationObj = location;
      this.location = location.locationPath;
    },
    gotoCityReservation() {
      this.$router.push({
        name: this.PAGES.CityReservationForm,
        params: {
          city: this.location,
          checkin: this.dates.Checkin,
          nights: this.dates.Nights,
          rooms: 1,
          adults: this.adults
        }
      });
    },
    hasLocationResult(hasLocationResult) {
      this.hideNonLocation = hasLocationResult;
    },
    selectDates(dates) {
      this.dates = dates;
    },
    submitSearch() {
      const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
      if (this.location != "") {
        if (
          this.selectedLocationObj.featureCode === "HTL" &&
          this.selectedLocationObj.hotelId &&
          this.selectedLocationObj.hotelId !== EMPTY_GUID
        ) {
          this.track("directHotelHome", {
            hotelCity: this.selectedLocationObj.admin1,
            hotelName: this.selectedLocationObj.name,
            hotelId: this.selectedLocationObj.id,
            checkin: this.dates.Checkin,
            nights: this.dates.Nights,
            adults: this.adults,
            rooms: this.rooms
          });
          this.$router.push({
            name: this.PAGES.HotelDetail,
            params: {
              locationPath: "direct",
              hotelId: this.selectedLocationObj.hotelId,
              checkin: this.dates.Checkin,
              nights: this.dates.Nights,
              refundable: true,
              adults: this.adults,
              rooms: this.rooms
            }
          });
        } else {
          this.track("hotelSearchHome", {
            city: this.selectedLocationObj.name,
            country: this.selectedLocationObj.country,
            checkin: this.dates.Checkin,
            nights: this.dates.Nights,
            adults: this.adults,
            rooms: this.rooms
          });
          this.$router.push({
            name: this.PAGES.SearchResult,
            params: {
              locationPath: this.selectedLocationObj.admin1 || this.location,
              checkin: this.dates.Checkin,
              nights: this.dates.Nights,
              refundable: true,
              adults: this.adults,
              rooms: this.rooms
            }
          });
        }
      }
    }
  }
};
</script>

<style lang="scss">
.home {
  margin: 0;
  background: center no-repeat;
  background-image: var(--brand-background-image);
  background-size: cover;
}

.logo--big {
  position: absolute;
  right: 0;
  height: 60px;
  top: 50%;
  margin-top: -30px;
}

.main-searchbar--input {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.main-searchbar__title {
  flex-grow: 0;
}

.main-searchbar__form {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  place-content: flex-end space-around;
}

.introjs-tooltip {
  box-shadow: -3px 0px 11px 3px #6e6b6b;
  padding: 1.2rem;
}

.introjs-tooltip-header {
  position: relative;
}

.introjs-skipbutton {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: content-box;
  position: absolute;
  padding: 0rem 2rem;
  border: 1px solid #bdbdbd;
  text-decoration: none;
  text-shadow: 1px 1px 0 #fff;
  font-size: 14px;
  color: #424242;
  white-space: nowrap;
  outline: 0;
  background-color: #f4f4f4;
  border-radius: 0.2em;
  zoom: 1;
}
</style>
